import type { TowerType } from '../core/types';

/**
 * Timed effects — the Diamond's shield and the Potion Shop's buffs (spec:
 * docs/superpowers/specs/2026-09-02-the-hub-design.md §3; Samurai's Taste and
 * Olive Oil: docs/superpowers/specs/2026-09-08-quests-and-potions-design.md).
 *
 * An effect is a RECORD with a wall-clock expiry: it starts the moment the
 * player presses Use, wherever they are, and keeps ticking through menus,
 * between levels and across reloads. Everything here is pure — the clock is
 * always passed in as `now` (epoch ms), so the purity test stays green and
 * the tests pin exact moments.
 *
 * The list persists as JSON under `hambi.effects` (ProgressService) and syncs
 * with the account save. GameScene turns it into a `ModifierTable` each frame
 * for the simulation; the HUD's corner timers and the icons above towers read
 * the same records.
 */

export type EffectKind =
  | 'diamond'
  | 'pickle'
  | 'chili'
  | 'cola'
  | 'burger'
  | 'samurai'
  | 'oliveoil'
  | 'debris'
  | 'moonstone'
  | 'goldstone'
  | 'capystone'
  | 'darkshard'
  | 'lightshard'
  // The Fungi Forest's two items (spec 2026-10-24): targeted, like the Pickle.
  | 'shroom'
  | 'snowshroom';

export const EFFECT_KINDS: readonly EffectKind[] = [
  'diamond',
  'pickle',
  'chili',
  'cola',
  'burger',
  'samurai',
  'oliveoil',
  'debris',
  'moonstone',
  'goldstone',
  'capystone',
  'darkshard',
  'lightshard',
  'shroom',
  'snowshroom',
];

export interface EffectRecord {
  kind: EffectKind;
  /** The tower type a targeted potion (pickle / chili / cola) was used on. */
  tower?: TowerType;
  /** Epoch ms at which the effect ends. */
  endsAt: number;
  /**
   * Only on an effect that does NOT tick while the game is closed (the Dark
   * Shard, `OFFLINE_PAUSED_EFFECTS`): epoch ms the game was last seen open
   * with it running. A gap since then longer than `EFFECT_OFFLINE_GRACE_MS`
   * is time spent offline, and pushes the end back by that much — see
   * `effectEnd` and `heartbeatEffects`.
   */
  seenAt?: number;
}

/** How long each effect runs from the moment of Use. */
export const EFFECT_DURATION_MS: Record<EffectKind, number> = {
  diamond: 60_000,
  pickle: 180_000,
  chili: 90_000,
  cola: 120_000,
  burger: 90_000,
  samurai: 120_000,
  oliveoil: 60_000,
  /** Space Debris: two minutes with no nacho able to touch a tower. */
  debris: 120_000,
  /** The Moon Stone: half an hour of moonlight over every tower. */
  moonstone: 1_800_000,
  /** The Golden Stone: the Moon Stone's half hour, in gold. */
  goldstone: 1_800_000,
  /** The Capystone: the Moon Stone's half hour, in capybara brown. */
  capystone: 1_800_000,
  /** The Dark Shard: two hours — of PLAYING time (OFFLINE_PAUSED_EFFECTS). */
  darkshard: 7_200_000,
  /** The Light Shard: the Dark Shard's two hours of play, in white. */
  lightshard: 7_200_000,
  /** The Shroom: one tower type dodges 20% of thrown attacks for 3:00. */
  shroom: 180_000,
  /** The Snowshroom: one tower type shroomnitizes an enemy every 15 s for 3:00. */
  snowshroom: 180_000,
};

/**
 * Effects whose clock stops while the game is closed: "time does not deplete
 * when you are offline" (the Dark Shard, spec 2026-10-07). Everything else is
 * wall-clock and keeps ticking through menus, between levels and across
 * reloads. "Online" is the game being OPEN, on any screen, in any tab state.
 */
export const OFFLINE_PAUSED_EFFECTS: readonly EffectKind[] = ['darkshard', 'lightshard'];

/** How often an open game stamps `seenAt` on a paused-offline effect. */
export const EFFECT_HEARTBEAT_MS = 60_000;
/**
 * A gap since the last stamp longer than this was the game being CLOSED (a
 * background tab still stamps, throttled to about once a minute). Up to this
 * much of a closing is counted as play — the price of not stamping more often,
 * which would push the whole save to the server every few seconds.
 */
export const EFFECT_OFFLINE_GRACE_MS = 150_000;

/**
 * When `e` really ends, as seen at `now`. A wall-clock effect ends at
 * `endsAt`. A paused-offline one that has not been stamped for longer than
 * the grace is mid-gap — the game was closed — and that whole gap is added,
 * so the shard does not run down (or vanish) before the next heartbeat moves
 * `endsAt` for good.
 */
export function effectEnd(e: EffectRecord, now: number): number {
  if (e.seenAt === undefined) return e.endsAt;
  const gap = now - e.seenAt;
  return gap > EFFECT_OFFLINE_GRACE_MS ? e.endsAt + gap : e.endsAt;
}

/**
 * The open game's heartbeat over the stored list: every paused-offline record
 * is stamped `seenAt = now`, and one that was last stamped longer ago than the
 * grace first has that gap added to `endsAt`. Returns null when the list holds
 * no such record (nothing to write). Expired records are left for
 * `activeEffects` to drop, as ever.
 */
export function heartbeatEffects(
  list: readonly EffectRecord[],
  now: number,
): EffectRecord[] | null {
  if (!list.some((e) => e.seenAt !== undefined)) return null;
  return list.map((e) =>
    e.seenAt === undefined ? e : { ...e, endsAt: effectEnd(e, now), seenAt: now },
  );
}

/** Effects that need a tower picked when used. */
export const TARGETED_EFFECTS: readonly EffectKind[] = [
  'pickle',
  'chili',
  'cola',
  'shroom',
  'snowshroom',
];

/** Towers COLA may not be used on. */
export const COLA_EXCLUDED: readonly TowerType[] = ['ninja'];

/**
 * The Hambis — every tower that IS Hambi in a costume. Bottle'l Burger buffs
 * exactly these; the Cheese Guardian, Saucemaster and the rest are not
 * hamburgers and go without.
 */
export const HAMBI_TOWERS: readonly TowerType[] = [
  'patty',
  'wise',
  'darkhambi',
  'ninja',
  'boxer',
  'gunner',
  'shrekbook',
  'phantom',
  'lightning',
  'spear',
  'bow',
  // A crowned Hambi is still Hambi in a costume.
  'kinghambi',
  // So are the Golden Takeout Bag's three.
  'dagger',
  'leafstorm',
  'samurai',
  // ...and its three burgers after them (spec 2026-09-29).
  'masher',
  'grease',
  'ultrahammy',
  // The Origin Event's Dark Shop three are burgers too (spec 2026-10-12).
  'horror',
  'demonic',
  'hamreaper',
  // ...and so are the Light Shop three (spec 2026-10-13).
  'healer',
  'banker',
  'angelic',
];

/**
 * Every tower whose behaviour is melee — Samurai's Taste buffs exactly these
 * (the Cheese Bladesmen included). Pinned to balance.ts by
 * tests/unit/effects.test.ts so the list cannot rot.
 */
export const MELEE_TOWERS: readonly TowerType[] = [
  'blade',
  'ninja',
  'boxer',
  'spear',
  'capy',
  'hog',
  // The Golden Takeout Bag's melee pair; Samurai Slicer is a Multi-attacker
  // whose SLASH is melee, so the potion buffs him too.
  'dagger',
  'samurai',
];

/**
 * Every tower there is — what the Moon Stone buffs. Pinned to the TowerType
 * union by tests/unit/effects.test.ts, so a new tower cannot be forgotten here.
 */
export const ALL_TOWERS: readonly TowerType[] = [
  'patty',
  'pickle',
  'cheese',
  'bacon',
  'sauce',
  'blade',
  'bomb',
  'wise',
  'wizard',
  'sniper',
  'darkhambi',
  'ninja',
  'sensei',
  'boxer',
  'gunner',
  'firemage',
  'aquasorcerer',
  'shrekbook',
  'phantom',
  'lightning',
  'spear',
  'bow',
  'capy',
  'generalcapy',
  'galacticcapy',
  'hog',
  'magecapy',
  'minigunner',
  'kingcapy',
  'kinghambi',
  'dagger',
  'leafstorm',
  'samurai',
  'masher',
  'grease',
  'ultrahammy',
  // The Origin Event's Dark Shop three are burgers too (spec 2026-10-12).
  'horror',
  'demonic',
  'hamreaper',
  // ...and so are the Light Shop three (spec 2026-10-13).
  'healer',
  'banker',
  'angelic',
  'goldenshroom',
];

/** The Moon Stone: every tower, whatever it is, hits 50% harder. */
export const MOONSTONE_DAMAGE_BONUS = 0.5;
/**
 * The Golden Stone: the Moon Stone's +50% on every tower. A separate kind, so
 * the two stack (+100%) as any two different effects do — but a second Golden
 * Stone is refused while the first still runs, like any duplicate.
 */
export const GOLDSTONE_DAMAGE_BONUS = 0.5;
/**
 * The Capystone (the Event Archive's prize, spec 2026-10-04): the Moon Stone's
 * +50% on every tower, as its own kind — so it stacks with a Moon Stone and a
 * Golden Stone, and a second Capystone is refused while the first runs.
 */
export const CAPYSTONE_DAMAGE_BONUS = 0.5;
/**
 * The Dark Shard (The Dark Forest's prize, spec 2026-10-07): every tower +10%
 * for two hours of play. Its own kind, so it stacks with the stones.
 */
export const DARKSHARD_DAMAGE_BONUS = 0.1;
/**
 * The Light Shard (The Sky Islands' prize, spec 2026-10-08): "the Dark Shard
 * but white, and does the same thing" — +10% on every tower for two hours of
 * play. Its own kind, so a Light and a Dark Shard run together (+20%).
 */
export const LIGHTSHARD_DAMAGE_BONUS = 0.1;
export const PICKLE_DAMAGE_BONUS = 0.25;
export const SAMURAI_DAMAGE_BONUS = 0.35;
export const BURGER_DAMAGE_BONUS = 0.25;
export const COLA_COOLDOWN_BONUS = 0.25;
/** Chili adds ten percentage points of slow (0.40 → 0.50). */
export const CHILI_SLOW_BONUS = 0.1;
/**
 * The Shroom (spec 2026-10-24): the tower type it is used on dodges this share
 * of the attacks THROWN at it (every enemy throw, potion and poison dose, and
 * a possessed tower's strike) — "DODGE" over the tower, nothing lands.
 */
export const SHROOM_DODGE_CHANCE = 0.2;
/**
 * The Snowshroom (spec 2026-10-24): every this-many seconds the tower type it
 * is used on SHROOMNITIZES the nearest enemy in its range, which then hits the
 * nearest OTHER enemy once for SNOWSHROOM_STRIKE_SHARE of its own max HP.
 */
export const SNOWSHROOM_COOLDOWN = 15;
/** Seconds from the shroomnitizing to the enemy's strike. */
export const SNOWSHROOM_STRIKE_DELAY = 1;
export const SNOWSHROOM_STRIKE_SHARE = 0.1;

/** Atlas key drawn for each effect (corner timer, tower icon, storage). */
export const EFFECT_ICON: Record<EffectKind, string> = {
  diamond: 'diamond',
  pickle: 'icon-pickle',
  chili: 'icon-jalapeno',
  cola: 'icon-soda',
  burger: 'icon-bun',
  samurai: 'icon-swords',
  /** One image serves shop and timer: the Olive Oil bottle IS its icon. */
  oliveoil: 'potion-oliveoil',
  /** The rocket, per the brief — not the black slab the debris itself is. */
  debris: 'icon-rocket',
  /** The moon that floats over every tower while the stone runs. */
  moonstone: 'icon-moon',
  /** The stone itself floats over every tower while it runs. */
  goldstone: 'item-goldstone',
  capystone: 'item-capystone',
  darkshard: 'item-darkshard',
  lightshard: 'item-lightshard',
  shroom: 'item-shroom',
  snowshroom: 'item-snowshroom',
};

export function isEffectKind(v: unknown): v is EffectKind {
  return typeof v === 'string' && (EFFECT_KINDS as readonly string[]).includes(v);
}

/** Parse a stored JSON list, dropping anything malformed. Never throws. */
export function parseEffects(raw: string | null): EffectRecord[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: EffectRecord[] = [];
  for (const e of parsed) {
    if (!e || typeof e !== 'object') continue;
    const r = e as Record<string, unknown>;
    if (!isEffectKind(r.kind)) continue;
    if (typeof r.endsAt !== 'number' || !Number.isFinite(r.endsAt)) continue;
    const rec: EffectRecord = { kind: r.kind, endsAt: r.endsAt };
    if (typeof r.tower === 'string') rec.tower = r.tower as TowerType;
    if (typeof r.seenAt === 'number' && Number.isFinite(r.seenAt)) rec.seenAt = r.seenAt;
    out.push(rec);
  }
  return out;
}

/** The effects still running at `now`, soonest-ending first (see `effectEnd`). */
export function activeEffects(list: readonly EffectRecord[], now: number): EffectRecord[] {
  return list
    .filter((e) => effectEnd(e, now) > now)
    .sort((a, b) => effectEnd(a, now) - effectEnd(b, now));
}

export type ApplyCheck = { ok: true } | { ok: false; reason: string };

/**
 * Whether `kind` may be used now (on `tower`, for a targeted kind). Different
 * kinds stack freely — even on the same tower — but the SAME kind on the same
 * tower (or a second Diamond / Burger, which have no tower) is refused while
 * the first still runs.
 */
export function canApply(
  list: readonly EffectRecord[],
  kind: EffectKind,
  tower: TowerType | undefined,
  now: number,
): ApplyCheck {
  const targeted = TARGETED_EFFECTS.includes(kind);
  if (targeted && !tower) return { ok: false, reason: 'Pick a tower first' };
  if (kind === 'cola' && tower && COLA_EXCLUDED.includes(tower)) {
    return { ok: false, reason: 'COLA does nothing for Ninja Hambi' };
  }
  const dup = activeEffects(list, now).some(
    (e) => e.kind === kind && (targeted ? e.tower === tower : true),
  );
  if (dup) return { ok: false, reason: 'Already active' };
  return { ok: true };
}

/** A new list with `kind` started at `now`. Caller checks `canApply` first. */
export function addEffect(
  list: readonly EffectRecord[],
  kind: EffectKind,
  tower: TowerType | undefined,
  now: number,
): EffectRecord[] {
  const rec: EffectRecord = { kind, endsAt: now + EFFECT_DURATION_MS[kind] };
  if (tower && TARGETED_EFFECTS.includes(kind)) rec.tower = tower;
  if (OFFLINE_PAUSED_EFFECTS.includes(kind)) rec.seenAt = now;
  return [...activeEffects(list, now), rec];
}

export interface TowerModifiers {
  /** Multiplies every shot's damage (1 = unchanged). */
  damageMult: number;
  /** Multiplies the firing clock (1.25 = 25% faster cooldowns). */
  cooldownMult: number;
  /** Added to the tower's slow magnitude, or a fresh aura of this strength. */
  slowBonus: number;
  /** The Shroom: the chance a thrown attack on this tower misses. */
  dodgeChance?: number;
  /** The Snowshroom: this tower shroomnitizes an enemy every SNOWSHROOM_COOLDOWN s. */
  shroomnitize?: boolean;
}

export interface ModifierTable {
  towers: Partial<Record<TowerType, TowerModifiers>>;
  /** The Diamond: every tower ignores fireball damage. */
  invulnerable: boolean;
  /**
   * Space Debris: no nacho (see core/enemy NACHO_TYPES) can affect a tower —
   * knives, lasers and stuns all stop. Narrower than `invulnerable`, which
   * shrugs off every attacker's damage but leaves stuns alone.
   */
  nachoImmune: boolean;
}

export const NO_MODIFIERS: ModifierTable = Object.freeze({
  towers: {},
  invulnerable: false,
  nachoImmune: false,
});

const BASE: TowerModifiers = { damageMult: 1, cooldownMult: 1, slowBonus: 0 };

/**
 * Fold the active records into per-tower multipliers for the simulation.
 * Pickle and Burger on one Hambi add (+25% + 25% = ×1.5); Pickle on top of
 * Burger for a non-Hambi does not exist because Burger never reaches it.
 */
export function effectsToModifiers(list: readonly EffectRecord[], now: number): ModifierTable {
  const towers: Partial<Record<TowerType, TowerModifiers>> = {};
  let invulnerable = false;
  let nachoImmune = false;
  const bump = (t: TowerType, f: (m: TowerModifiers) => void) => {
    const m = towers[t] ?? { ...BASE };
    f(m);
    towers[t] = m;
  };
  for (const e of activeEffects(list, now)) {
    switch (e.kind) {
      case 'diamond':
        invulnerable = true;
        break;
      case 'pickle':
        if (e.tower) bump(e.tower, (m) => (m.damageMult += PICKLE_DAMAGE_BONUS));
        break;
      case 'chili':
        if (e.tower) bump(e.tower, (m) => (m.slowBonus += CHILI_SLOW_BONUS));
        break;
      case 'cola':
        if (e.tower) bump(e.tower, (m) => (m.cooldownMult += COLA_COOLDOWN_BONUS));
        break;
      case 'burger':
        for (const t of HAMBI_TOWERS) bump(t, (m) => (m.damageMult += BURGER_DAMAGE_BONUS));
        break;
      case 'samurai':
        for (const t of MELEE_TOWERS) bump(t, (m) => (m.damageMult += SAMURAI_DAMAGE_BONUS));
        break;
      case 'oliveoil':
        // Olive Oil is the Diamond's immunity in potion form.
        invulnerable = true;
        break;
      case 'debris':
        nachoImmune = true;
        break;
      case 'moonstone':
        // EVERY tower, not just the Hambis or the melee: the brief puts the
        // moon over all of them.
        for (const t of ALL_TOWERS) bump(t, (m) => (m.damageMult += MOONSTONE_DAMAGE_BONUS));
        break;
      case 'goldstone':
        for (const t of ALL_TOWERS) bump(t, (m) => (m.damageMult += GOLDSTONE_DAMAGE_BONUS));
        break;
      case 'capystone':
        for (const t of ALL_TOWERS) bump(t, (m) => (m.damageMult += CAPYSTONE_DAMAGE_BONUS));
        break;
      case 'darkshard':
        for (const t of ALL_TOWERS) bump(t, (m) => (m.damageMult += DARKSHARD_DAMAGE_BONUS));
        break;
      case 'lightshard':
        for (const t of ALL_TOWERS) bump(t, (m) => (m.damageMult += LIGHTSHARD_DAMAGE_BONUS));
        break;
      case 'shroom':
        if (e.tower) bump(e.tower, (m) => (m.dodgeChance = SHROOM_DODGE_CHANCE));
        break;
      case 'snowshroom':
        if (e.tower) bump(e.tower, (m) => (m.shroomnitize = true));
        break;
    }
  }
  return { towers, invulnerable, nachoImmune };
}

/**
 * Effect icons to float above a tower of `type`: one per active targeted
 * potion on it, plus the bun while a Burger runs and the tower is a Hambi,
 * and the swords while Samurai's Taste runs and the tower is melee. Diamond
 * and Olive Oil are not per-tower (the blue star is drawn by the shield pass).
 */
export function towerEffectIcons(
  list: readonly EffectRecord[],
  type: TowerType,
  now: number,
): string[] {
  const icons: string[] = [];
  for (const e of activeEffects(list, now)) {
    // Diamond, Olive Oil and Space Debris are map-wide, not per-tower.
    if (e.kind === 'diamond' || e.kind === 'oliveoil' || e.kind === 'debris') continue;
    // The Moon Stone IS per-tower, and it is over every one of them.
    if (
      e.kind === 'moonstone' ||
      e.kind === 'goldstone' ||
      e.kind === 'capystone' ||
      e.kind === 'darkshard' ||
      e.kind === 'lightshard'
    ) {
      icons.push(EFFECT_ICON[e.kind]);
      continue;
    }
    if (e.kind === 'burger') {
      if (HAMBI_TOWERS.includes(type)) icons.push(EFFECT_ICON.burger);
      continue;
    }
    if (e.kind === 'samurai') {
      if (MELEE_TOWERS.includes(type)) icons.push(EFFECT_ICON.samurai);
      continue;
    }
    if (e.tower === type) icons.push(EFFECT_ICON[e.kind]);
  }
  // Stable kind order (pickle, chili, cola, burger, samurai) whatever their expiry.
  const order = (k: string) => Object.values(EFFECT_ICON).indexOf(k);
  return [...new Set(icons)].sort((a, b) => order(a) - order(b));
}

/** `m:ss` (or `h:mm:ss` from an hour up — the Dark Shard), floored, never negative. */
export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, '0');
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}
